import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ANEXOS_SIMPLES,
  AREAS_ATUACAO,
  formatarCnpj,
  formatarRaizCnpj,
  percentualPpm,
  PERFIS_SERVICO,
  REGIMES_TRIBUTARIOS,
  REQUISITO_PERFIL_SERVICO,
  ROTULO_PERFIL_SERVICO,
  ROTULO_AREA,
  ROTULO_REGIME,
  type AnexoSimples,
  type AreaAtuacao,
  type PerfilServico,
  type RegimeTributario,
} from '@via-tax/contracts'
import { Aviso, Botao, Campo, Selecao, Selo } from '@via-tax/ui'
import { useState } from 'react'
import { reais } from '../formatos'
import type { ContextoSessao, DetalheEmpresa } from '../tipos'
import { mensagemDeErro, useTRPC } from '../trpc'

const SITUACAO: Record<string, string> = {
  active: 'Ativa',
  inactive: 'Inativa',
  onboarding: 'Em implantação',
  constitution: 'Em constituição',
}

export function Empresa({ contexto }: { contexto: ContextoSessao }) {
  const trpc = useTRPC()
  const detalhe = useQuery(trpc.empresa.detalhe.queryOptions())

  if (detalhe.isPending) return <p className="text-sm text-tinta-3">Carregando…</p>
  if (detalhe.isError) return <Aviso>{mensagemDeErro(detalhe.error)}</Aviso>
  // key: ao trocar de empresa ou salvar, o formulário renasce com os valores do servidor.
  const d = detalhe.data
  return <FormEmpresa key={`${d.id}:${d.atualizadoEm}`} e={d} podeEditar={(contexto.capacidades as string[]).includes('editar_empresa')} />
}

function FormEmpresa({ e, podeEditar }: { e: DetalheEmpresa; podeEditar: boolean }) {
  const trpc = useTRPC()
  const qc = useQueryClient()
  const [regime, setRegime] = useState<RegimeTributario | ''>(e.regimeTributario ?? '')
  const [area, setArea] = useState<AreaAtuacao | ''>(e.areaAtuacao ?? '')
  const [anexo, setAnexo] = useState<AnexoSimples | ''>(e.anexoSimples ?? '')
  const [perfil, setPerfil] = useState<PerfilServico | ''>(e.perfilServico ?? '')
  const [requisitos, setRequisitos] = useState<boolean>(e.requisitosPerfil)
  const sugestao = useQuery(trpc.empresa.sugestaoPerfil.queryOptions())
  const [salvo, setSalvo] = useState(false)

  const salvar = useMutation(
    trpc.empresa.atualizar.mutationOptions({
      onSuccess: () => {
        setSalvo(true)
        void qc.invalidateQueries({ queryKey: trpc.empresa.detalhe.queryKey() })
        void qc.invalidateQueries({ queryKey: trpc.sessao.contexto.queryKey() })
      },
    }),
  )

  const sugestoes = (e.origemSugestoes ?? {}) as { regimeTributario?: RegimeTributario | null; taxRegimeOrigem?: string | null }
  const divergeDaOrigem = sugestoes.regimeTributario && e.regimeTributario && sugestoes.regimeTributario !== e.regimeTributario
  const alterado =
    (regime || null) !== e.regimeTributario ||
    (area || null) !== e.areaAtuacao ||
    ((regime === 'simples_nacional' ? anexo : '') || null) !== e.anexoSimples ||
    (perfil || null) !== e.perfilServico ||
    (perfil ? requisitos : false) !== e.requisitosPerfil
  const requisito = perfil ? REQUISITO_PERFIL_SERVICO[perfil] : null
  const sug = sugestao.data?.sugestao

  return (
    <div className="flex flex-col gap-6">
      <header>
        <p className="num text-xs text-tinta-3">{formatarCnpj(e.cnpj)}</p>
        <h1 className="text-xl font-semibold tracking-tight">Dados da empresa</h1>
      </header>

      <section className="rounded-lg border border-linha bg-painel">
        <div className="flex items-center justify-between border-b border-linha px-5 py-3">
          <h2 className="text-sm font-semibold">Cadastro</h2>
          {e.origem === 'processos' ? <Selo>Vem do Processos · só leitura</Selo> : <Selo>Cadastro manual</Selo>}
        </div>
        <dl className="grid gap-x-8 gap-y-4 p-5 sm:grid-cols-2">
          <Dado rotulo="Razão social" valor={e.razaoSocial} />
          <Dado rotulo="Nome fantasia" valor={e.nomeFantasia} />
          <Dado rotulo="CNPJ da matriz" valor={formatarCnpj(e.cnpj)} mono />
          <Dado rotulo="Situação no escritório" valor={e.situacaoCadastral ? (SITUACAO[e.situacaoCadastral] ?? e.situacaoCadastral) : null} />
          <Dado rotulo="Município / UF" valor={[e.municipio, e.uf].filter(Boolean).join(' / ') || null} />
          <Dado rotulo="Responsável no escritório" valor={e.responsavelEscritorio} />
          <Dado rotulo="Inscrição estadual" valor={e.ie} mono />
          <Dado
            rotulo="Última sincronização"
            valor={e.sincronizadoEm ? new Date(e.sincronizadoEm).toLocaleString('pt-BR') : null}
          />
        </dl>
      </section>

      <section className="rounded-lg border border-linha bg-painel">
        <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-linha px-5 py-3">
          <h2 className="text-sm font-semibold">
            Estabelecimentos <span className="num font-normal text-tinta-3">· raiz {formatarRaizCnpj(e.cnpjRaiz)}</span>
          </h2>
          <p className="text-xs text-tinta-3">
            Documentos ficam separados por unidade; simulação, produtos e fornecedores são da empresa inteira.
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[560px] text-left text-[13px]">
            <thead className="text-[11px] uppercase tracking-[0.06em] text-tinta-3">
              <tr className="border-b border-linha">
                <th className="px-5 py-2 font-medium">Unidade</th>
                <th className="px-5 py-2 font-medium">CNPJ</th>
                <th className="px-5 py-2 font-medium">Município / UF</th>
                <th className="px-5 py-2 font-medium">Situação</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-linha">
              {e.estabelecimentos.map((u) => (
                <tr key={u.id}>
                  <td className="px-5 py-2.5">
                    <div className="flex items-center gap-2">
                      <Selo tom={u.matriz ? 'navy' : 'neutro'}>{u.matriz ? 'Matriz' : 'Filial'}</Selo>
                      <span className="truncate">{u.nomeFantasia || u.nome}</span>
                    </div>
                  </td>
                  <td className="num px-5 py-2.5 whitespace-nowrap">{formatarCnpj(u.cnpj)}</td>
                  <td className="px-5 py-2.5">{[u.municipio, u.uf].filter(Boolean).join(' / ') || <span className="text-tinta-3">—</span>}</td>
                  <td className="px-5 py-2.5">{u.situacaoCadastral ? (SITUACAO[u.situacaoCadastral] ?? u.situacaoCadastral) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!e.estabelecimentos.some((u) => u.matriz) && (
          <p className="border-t border-linha px-5 py-2.5 text-xs text-tinta-3">
            A matriz (ordem 0001) ainda não veio do Processos. Os dados do cadastro acima são da primeira unidade conhecida.
          </p>
        )}
      </section>

      <section className="rounded-lg border border-linha bg-painel">
        <div className="border-b border-linha px-5 py-3">
          <h2 className="text-sm font-semibold">Perfil para a simulação</h2>
          <p className="mt-0.5 text-xs text-tinta-3">
            Definido no Via Tax. O Processos só sugere o valor inicial — mudança lá não sobrescreve o que foi decidido aqui.
          </p>
        </div>
        <form
          className="grid gap-4 p-5 sm:grid-cols-3"
          onSubmit={(ev) => {
            ev.preventDefault()
            salvar.mutate({
              regimeTributario: regime || null,
              areaAtuacao: area || null,
              anexoSimples: regime === 'simples_nacional' ? anexo || null : null,
              perfilServico: perfil || null,
              requisitosPerfil: perfil ? requisitos : false,
            })
          }}
        >
          <Campo rotulo="Regime tributário">
            <Selecao value={regime} disabled={!podeEditar} onChange={(ev) => setRegime(ev.target.value as RegimeTributario | '')}>
              <option value="">Não definido</option>
              {REGIMES_TRIBUTARIOS.map((r) => (
                <option key={r} value={r}>
                  {ROTULO_REGIME[r]}
                </option>
              ))}
            </Selecao>
          </Campo>
          <Campo rotulo="Anexo do Simples" dica={regime === 'simples_nacional' ? 'Define a repartição da CBS no DAS' : 'Só para Simples Nacional'}>
            <Selecao value={regime === 'simples_nacional' ? anexo : ''} disabled={!podeEditar || regime !== 'simples_nacional'} onChange={(ev) => setAnexo(ev.target.value as AnexoSimples | '')}>
              <option value="">—</option>
              {ANEXOS_SIMPLES.map((a) => (
                <option key={a} value={a}>
                  Anexo {a}
                </option>
              ))}
            </Selecao>
          </Campo>
          <Campo rotulo="Área de atuação">
            <Selecao value={area} disabled={!podeEditar} onChange={(ev) => setArea(ev.target.value as AreaAtuacao | '')}>
              <option value="">Não definida</option>
              {AREAS_ATUACAO.map((a) => (
                <option key={a} value={a}>
                  {ROTULO_AREA[a]}
                </option>
              ))}
            </Selecao>
          </Campo>

          <div className="flex flex-col gap-3 sm:col-span-3">
            <Campo
              rotulo="Perfil dos serviços prestados"
              dica="Muda a presunção do Lucro Presumido (8% ou 32%), a redução da CBS/IBS (saúde −60%, profissão intelectual −30%) e o anexo do Simples (construção no IV)."
            >
              <Selecao value={perfil} disabled={!podeEditar} onChange={(ev) => setPerfil(ev.target.value as PerfilServico | '')}>
                <option value="">Sem perfil específico (comércio, indústria ou serviço em geral)</option>
                {PERFIS_SERVICO.map((p) => (
                  <option key={p} value={p}>
                    {ROTULO_PERFIL_SERVICO[p]}
                  </option>
                ))}
              </Selecao>
            </Campo>
            {requisito && (
              <label className="flex items-start gap-2 rounded-md border border-linha bg-fundo px-3 py-2 text-[13px]">
                <input type="checkbox" className="mt-0.5" checked={requisitos} disabled={!podeEditar} onChange={(ev) => setRequisitos(ev.target.checked)} />
                <span>
                  <strong>Confirmado pelo escritório:</strong> {requisito}
                </span>
              </label>
            )}
            {sug && sug.perfil !== perfil && (
              <Aviso tom="info">
                As notas de serviço dos últimos 12 meses sugerem <strong>{ROTULO_PERFIL_SERVICO[sug.perfil]}</strong> ({percentualPpm(sug.participacao, 0)} do faturamento de serviços).{' '}
                {podeEditar && (
                  <button type="button" className="underline" onClick={() => setPerfil(sug.perfil)}>
                    Usar esta sugestão
                  </button>
                )}
              </Aviso>
            )}
            {sugestao.data && sugestao.data.itens.length > 0 && (
              <details className="text-xs text-tinta-2">
                <summary className="cursor-pointer text-navy">Códigos de serviço nas NFS-e emitidas ({reais(sugestao.data.totalServicos)} em 12 meses)</summary>
                <ul className="mt-2 flex flex-col gap-1">
                  {sugestao.data.itens.map((x) => (
                    <li key={x.codigo} className="flex flex-wrap justify-between gap-2 border-t border-linha pt-1">
                      <span className="min-w-0">
                        <span className="num">{x.item ?? x.codigo}</span> · {x.descricao}
                      </span>
                      <span className="num whitespace-nowrap">
                        {reais(x.valor)}
                        {x.perfil ? ` · ${ROTULO_PERFIL_SERVICO[x.perfil].split(/[:(]/)[0]?.trim() ?? ''}` : ''}
                      </span>
                    </li>
                  ))}
                </ul>
              </details>
            )}
            {divergeDaOrigem && (
              <Aviso tom="info">
                No Processos o regime está como {ROTULO_REGIME[sugestoes.regimeTributario!]}
                {sugestoes.taxRegimeOrigem ? ` (${sugestoes.taxRegimeOrigem})` : ''}. Aqui vale {ROTULO_REGIME[e.regimeTributario!]}.
              </Aviso>
            )}
            {salvar.isError && <Aviso>{mensagemDeErro(salvar.error)}</Aviso>}
            {salvo && !alterado && <Aviso tom="ok">Perfil salvo e registrado na trilha de auditoria.</Aviso>}
            {podeEditar ? (
              <div>
                <Botao type="submit" variante="primario" disabled={!alterado || salvar.isPending}>
                  {salvar.isPending ? 'Salvando…' : 'Salvar perfil'}
                </Botao>
              </div>
            ) : (
              <p className="text-xs text-tinta-3">Somente o escritório altera o perfil da simulação.</p>
            )}
          </div>
        </form>
      </section>
    </div>
  )
}

function Dado({ rotulo, valor, mono }: { rotulo: string; valor: string | null | undefined; mono?: boolean }) {
  return (
    <div className="min-w-0">
      <dt className="text-[11px] font-medium uppercase tracking-[0.06em] text-tinta-3">{rotulo}</dt>
      <dd className={`mt-0.5 break-words text-[13px] ${mono ? 'num' : ''} ${valor ? '' : 'text-tinta-3'}`}>{valor || 'Não informado'}</dd>
    </div>
  )
}
